import React from 'react';
import { BASE_URL } from '../Api';
import { Helmet, HelmetProvider } from "react-helmet-async";
import FreeHighlight from '../partials/FreeHighlight';

const DualCurrencyPlugin = () => {
  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Dual currency - Aronium</title>
          <meta name="description" content="Print receipts with second currency using Dual Currency plugin" />
        </Helmet>
      </HelmetProvider>

      <section className="py-8 max-w-screen-xl px-4 mx-auto">
        <div className="max-w-screen-xl mx-auto text-center">
          <h1 className="text-4xl mb-4">
            Effortlessly manage dual currencies
          </h1>
          <p>
            Dual Currency plugin provides a way to see prices and print receipt total in a second currency
          </p>

          {/* Call to Action Buttons */}
          <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
            {/* Download for Aronium Lite Button */}
            <a
              href={`${BASE_URL}/plugins/dual-currency/latest/Aronium.Pos.DualCurrency.Lite.exe`}
              className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full text-white bg-gradient-to-r from-[#3C6E8F] to-[#5a8bb2] hover:bg-gradient-to-l transition duration-300 shadow-lg focus:ring-4 focus:ring-blue-300 focus:outline-none hover:no-underline"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
              Download for Aronium Lite
            </a>

            {/* Download for Aronium Pro Button */}
            <a
              href={`${BASE_URL}/plugins/dual-currency/latest/Aronium.Pos.DualCurrency.Pro.exe`}
              className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full text-white bg-gradient-to-r from-[#607D8B] to-[#89a0a8] hover:bg-gradient-to-l transition duration-300 shadow-lg focus:ring-4 focus:ring-blue-200 focus:outline-none hover:no-underline"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
              Download for Aronium Pro
            </a>
          </div>
        </div>
      </section>

      <div className="flex flex-col text-center my-12 mx-4">
        <div className="grid place-content-center justify-items-center">
          <img src="/images/dual-currency.png" alt="Expiration date screen" className='rounded-lg xl:max-w-6xl' />
        </div>
      </div>

      {/* Overview Section */}
      <div className="py-10 bg-gray-100">
        <div className="max-w-7xl mx-auto text-center px-8 lg:px-12">
          <h2 className="text-4xl text-gray-800 leading-tight mb-8">
            Why use <span className='font-semibold'>Dual currency</span> plugin?
          </h2>
          <p className="text-lg text-gray-600 max-w-5xl mx-auto">
            The <span className='font-semibold'>Dual currency</span> plugin allows you to display prices and print receipt totals in a second currency.
          </p>
          <p className="text-lg text-gray-600 max-w-5xl mx-auto mt-4">Along with printing receipt totals in both primary and secondary currencies, you will be able to see prices in a second currency during sales, providing clarity for customers and facilitating smoother transactions.</p>
        </div>
        <FreeHighlight className="mt-12" />
      </div>

      {/* Key Features Section */}
      <div className="py-24">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          <div className="bg-white p-8 shadow-lg rounded-lg border">
            <h3 className="text-2xl font-semibold text-gray-800">
              Customizable currency format
            </h3>
            <p className="mt-4 text-gray-600">
              Select the country for the second currency to load its default format or use currency symbol of your choice.
            </p>
          </div>
          <div className="bg-white p-8 shadow-lg rounded-lg border">
            <h3 className="text-2xl font-semibold text-gray-800">
              Exchange rate management
            </h3>
            <p className="mt-4 text-gray-600">
              Enter the current exchange rate directly in the settings or on application start.
            </p>
          </div>

        </div>
      </div>

      {/* Call to Action Section */}
      <div className="relative bg-gradient-to-tr from-[#345678] to-[#abcdef] py-24 text-center text-white">
        <div className="relative max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-6xl mb-6">
            Ready to embrace global sales?
          </h2>
          <p className="text-xl mb-10">
            Start using the <span className='font-semibold'>Dual currency</span> plugin to provide your customers with clear, dual-currency receipts.
          </p>
          <a
            href="https://help.aronium.com/hc/en-us/articles/18638435054610-Configuring-dual-currency"
            className="button-rounded-white px-12 py-4"
            target='_blank'
            rel="noreferrer"
          >
            Learn more <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="ml-2 w-6 h-6 transition-all">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
            </svg>
          </a>
        </div>
      </div>
    </>
  );
};

export default DualCurrencyPlugin;
